import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, Copy } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/pages/admin/ImageUploader";
import LangToggle from "@/pages/admin/LangToggle";
import { useAdminSave } from "@/lib/adminSaveContext";

const inputCls =
  "w-full bg-transparent border border-ink/25 px-3 py-2 outline-none focus:border-ink transition-colors text-sm";

function StringList({ value = [], onChange, testid }) {
  return (
    <div className="space-y-2">
      {value.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input
            className={inputCls}
            value={v}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))}
            data-testid={testid ? `${testid}-${i}` : undefined}
          />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="px-2 text-red-700 hover:bg-red-700/10">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, ""])} className="eyebrow inline-flex items-center gap-1 border border-ink/25 px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors">
        <Plus size={12} /> Adaugă
      </button>
    </div>
  );
}

/* Dynamic lesson rows (unlimited) — module-level to keep input identity stable (no focus loss). */
function LessonsEditor({ value = [], onChange }) {
  const lessons = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(lessons.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(lessons.filter((_, j) => j !== i));
  const moveRow = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= lessons.length) return;
    const a = lessons.slice();
    const [x] = a.splice(i, 1);
    a.splice(to, 0, x);
    onChange(a);
  };
  const add = () => {
    onChange([...lessons, { title_ro: "", title_en: "", youtube_url: "", thumbnail: "" }]);
  };
  return (
    <div className="space-y-4" data-testid="lessons-editor">
      <div className="flex items-center justify-between">
        <span className="eyebrow text-ink/40">{lessons.length} lecții</span>
        <button type="button" onClick={add} data-testid="lesson-add-top" className="eyebrow inline-flex items-center gap-1 border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors">
          <Plus size={13} /> Adaugă lecție
        </button>
      </div>
      {lessons.map((l, i) => (
        <div key={i} className="border border-ink/20 p-3" data-testid={`lesson-row-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-display text-2xl text-ink/40 tnum">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => moveRow(i, -1)} className="p-1 border border-ink/20 hover:bg-ink hover:text-paper transition-colors"><ChevronUp size={13} /></button>
              <button type="button" onClick={() => moveRow(i, 1)} className="p-1 border border-ink/20 hover:bg-ink hover:text-paper transition-colors"><ChevronDown size={13} /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`lesson-remove-${i}`} className="p-1 border border-red-700/40 text-red-700 hover:bg-red-700 hover:text-paper transition-colors"><Trash2 size={13} /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <input className={inputCls} placeholder="Titlu lecție (RO)" value={l.title_ro || ""} onChange={(e) => update(i, { title_ro: e.target.value })} data-testid={`lesson-title-ro-${i}`} />
            <input className={inputCls} placeholder="Lesson title (EN)" value={l.title_en || ""} onChange={(e) => update(i, { title_en: e.target.value })} data-testid={`lesson-title-en-${i}`} />
          </div>
          <input className={inputCls + " mb-2"} placeholder="Link video YouTube" value={l.youtube_url || ""} onChange={(e) => update(i, { youtube_url: e.target.value })} data-testid={`lesson-yt-${i}`} />
          <ImageUploader label="Thumbnail (opțional)" value={l.thumbnail || ""} onChange={(v) => update(i, { thumbnail: v })} testid={`lesson-thumb-${i}`} />
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button type="button" onClick={add} data-testid="lesson-add" className="eyebrow inline-flex items-center gap-1 border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors">
          <Plus size={13} /> Adaugă lecție
        </button>
        <span className="eyebrow text-ink/40">{lessons.length} lecții</span>
      </div>
    </div>
  );
}

export default function EntityManager({ title, endpoint, fields, emptyItem, lang, setLang, sideBySide = false, nameKey = "title", addLabel = "Adaugă", homeCount = 0 }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);
  const { registerSaver } = useAdminSave();

  const load = useCallback(async () => {
    const { data } = await api.get(`${endpoint}?include_unpublished=true`);
    setItems(data);
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = useCallback(async () => {
    if (editingId) {
      await api.put(`${endpoint}/${editingId}`, form);
    } else if (JSON.stringify(form) !== JSON.stringify(emptyItem)) {
      await api.post(endpoint, form);
    } else {
      return true; // nothing to save
    }
    setForm(emptyItem);
    setEditingId(null);
    await load();
    return true;
  }, [editingId, form, emptyItem, endpoint, load]);

  // Register with the top-bar Save/Publish so they also flush this open form.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => {
    registerSaver(() => persistRef.current());
    return () => registerSaver(null);
  }, [registerSaver]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const creating = !editingId;
      await persist();
      toast.success(creating ? "Creat" : "Actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare");
    }
  };

  const edit = (it) => {
    setEditingId(it.id);
    setForm({ ...emptyItem, ...it });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Ștergi acest element?")) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success("Șters");
    if (editingId === id) {
      setForm(emptyItem);
      setEditingId(null);
    }
    load();
  };

  const startNew = () => {
    setForm(emptyItem);
    setEditingId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // First N published items (sorted by order) = the ones shown on Home.
  const homeIds = homeCount
    ? items.filter((it) => it.published !== false).slice(0, homeCount).map((it) => it.id)
    : [];

  const duplicate = (it) => {
    const clone = { ...emptyItem, ...it };
    delete clone.id;
    delete clone.created_at;
    if (clone.slug) clone.slug = `${clone.slug}-copy`;
    if (clone.slug_en) clone.slug_en = `${clone.slug_en}-copy`;
    if (clone.slug_ro) clone.slug_ro = `${clone.slug_ro}-copy`;
    if (clone[`${nameKey}_ro`]) clone[`${nameKey}_ro`] = `${clone[`${nameKey}_ro`]} (Copie)`;
    if (clone[`${nameKey}_en`]) clone[`${nameKey}_en`] = `${clone[`${nameKey}_en`]} (Copy)`;
    clone.published = false;
    setForm(clone);
    setEditingId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Șablon duplicat — revizuiește slug-ul și apasă Creează");
  };

  const move = async (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= items.length) return;
    const a = items[index];
    const b = items[to];
    const aOrder = a.order ?? index;
    const bOrder = b.order ?? to;
    const same = aOrder === bOrder;
    try {
      await Promise.all([
        api.put(`${endpoint}/${a.id}`, { ...a, order: same ? to : bOrder }),
        api.put(`${endpoint}/${b.id}`, { ...b, order: same ? index : aOrder }),
      ]);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reordonare eșuată");
    }
  };

  const renderField = (f) => {
    const L = lang.toUpperCase();
    if (f.type === "select")
      return (
        <label className="block">
          <span className="eyebrow text-ink/50">{f.label}</span>
          <select
            className={inputCls + " mt-1"}
            value={form[f.k] ?? ""}
            onChange={(e) => set(f.k, e.target.value)}
            data-testid={`ef-${f.k}`}
          >
            {(f.options || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      );
    if (f.type === "text" || f.type === "number")
      return (
        <label className="block">
          <span className="eyebrow text-ink/50">{f.label}</span>
          <input
            type={f.type === "number" ? "number" : "text"}
            className={inputCls + " mt-1"}
            value={form[f.k] ?? ""}
            onChange={(e) => set(f.k, f.type === "number" ? Number(e.target.value) : e.target.value)}
            required={f.required}
            data-testid={`ef-${f.k}`}
          />
        </label>
      );
    if (f.type === "bool")
      return (
        <label className="flex items-center gap-3 eyebrow">
          <input type="checkbox" checked={!!form[f.k]} onChange={(e) => set(f.k, e.target.checked)} data-testid={`ef-${f.k}`} />
          {f.label}
        </label>
      );
    if (f.type === "bi")
      return sideBySide ? (
        <div className="block">
          <span className="eyebrow text-ink/50">{f.label}</span>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <input className={inputCls} placeholder="RO" value={form[`${f.k}_ro`] ?? ""} onChange={(e) => set(`${f.k}_ro`, e.target.value)} required={f.required} data-testid={`ef-${f.k}-ro`} />
            <input className={inputCls} placeholder="EN" value={form[`${f.k}_en`] ?? ""} onChange={(e) => set(`${f.k}_en`, e.target.value)} data-testid={`ef-${f.k}-en`} />
          </div>
        </div>
      ) : (
        <label className="block">
          <span className="eyebrow text-ink/50">{f.label} ({L})</span>
          <input className={inputCls + " mt-1"} value={form[`${f.k}_${lang}`] ?? ""} onChange={(e) => set(`${f.k}_${lang}`, e.target.value)} required={f.required} data-testid={`ef-${f.k}`} />
        </label>
      );
    if (f.type === "bi-area")
      return sideBySide ? (
        <div className="block">
          <span className="eyebrow text-ink/50">{f.label}</span>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <textarea rows={f.big ? 6 : 3} className={inputCls + " resize-y"} placeholder="RO" value={form[`${f.k}_ro`] ?? ""} onChange={(e) => set(`${f.k}_ro`, e.target.value)} data-testid={`ef-${f.k}-ro`} />
            <textarea rows={f.big ? 6 : 3} className={inputCls + " resize-y"} placeholder="EN" value={form[`${f.k}_en`] ?? ""} onChange={(e) => set(`${f.k}_en`, e.target.value)} data-testid={`ef-${f.k}-en`} />
          </div>
        </div>
      ) : (
        <label className="block">
          <span className="eyebrow text-ink/50">{f.label} ({L})</span>
          <textarea rows={f.big ? 6 : 3} className={inputCls + " mt-1 resize-y"} value={form[`${f.k}_${lang}`] ?? ""} onChange={(e) => set(`${f.k}_${lang}`, e.target.value)} data-testid={`ef-${f.k}`} />
        </label>
      );
    if (f.type === "bi-list")
      return sideBySide ? (
        <div>
          <div className="eyebrow text-ink/50 mb-2">{f.label}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="eyebrow text-ink/35 mb-1">RO</div><StringList value={form[`${f.k}_ro`] || []} onChange={(v) => set(`${f.k}_ro`, v)} testid={`ef-${f.k}-ro`} /></div>
            <div><div className="eyebrow text-ink/35 mb-1">EN</div><StringList value={form[`${f.k}_en`] || []} onChange={(v) => set(`${f.k}_en`, v)} testid={`ef-${f.k}-en`} /></div>
          </div>
        </div>
      ) : (
        <div>
          <div className="eyebrow text-ink/50 mb-2">{f.label} ({L})</div>
          <StringList value={form[`${f.k}_${lang}`] || []} onChange={(v) => set(`${f.k}_${lang}`, v)} testid={`ef-${f.k}`} />
        </div>
      );
    if (f.type === "lessons")
      return (
        <div>
          <div className="eyebrow text-ink/50 mb-2">{f.label}</div>
          <LessonsEditor value={form[f.k] || []} onChange={(v) => set(f.k, v)} />
        </div>
      );
    if (f.type === "image")
      return (
        <div>
          <div className="eyebrow text-ink/50 mb-2">{f.label}</div>
          <ImageUploader value={form[f.k]} onChange={(v) => set(f.k, v)} testid={`ef-${f.k}`} />
        </div>
      );
    if (f.type === "image-list") {
      const arr = form[f.k] || [];
      return (
        <div>
          <div className="eyebrow text-ink/50 mb-2">{f.label}</div>
          <div className="space-y-3">
            {arr.map((img, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1">
                  <ImageUploader value={img} onChange={(v) => set(f.k, arr.map((x, j) => (j === i ? v : x)))} />
                </div>
                <button type="button" onClick={() => set(f.k, arr.filter((_, j) => j !== i))} className="px-2 py-2 text-red-700 hover:bg-red-700/10">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => set(f.k, [...arr, ""])} className="eyebrow inline-flex items-center gap-1 border border-ink/25 px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors">
              <Plus size={12} /> Adaugă imagine
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="eyebrow">{editingId ? `Editează · ${title}` : `Adaugă · ${title}`}</div>
          {setLang && !sideBySide && <LangToggle lang={lang} setLang={setLang} testidPrefix="entity-form" />}
        </div>
        <form onSubmit={save} className="space-y-4" data-testid="entity-form">
          {fields.map((f) => (
            <div key={f.k}>{renderField(f)}</div>
          ))}
          <div className="flex gap-3 pt-2">
            <button type="submit" data-testid="entity-save" className="eyebrow border border-ink px-6 py-3 hover:bg-ink hover:text-paper transition-colors">
              {editingId ? "Salvează" : "Creează"}
            </button>
            {editingId && (
              <button type="button" onClick={() => { setForm(emptyItem); setEditingId(null); }} className="eyebrow px-6 py-3 hover:text-ink">
                Anulează
              </button>
            )}
          </div>
        </form>
      </div>

      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="eyebrow">{title} ({items.length})</div>
          <button
            type="button"
            onClick={startNew}
            data-testid="entity-add-new"
            className="eyebrow inline-flex items-center gap-1.5 border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors"
          >
            <Plus size={13} /> {addLabel}
          </button>
        </div>
        {homeCount > 0 && (
          <p className="eyebrow text-ink/45 mb-3 leading-relaxed">
            Primele {homeCount} elemente publicate (marcate <span className="text-ink">PE HOME</span>) apar pe pagina principală. Folosește ▲/▼ ca să alegi care și în ce ordine.
          </p>
        )}
        <div className="border border-line">
          {items.map((it, index) => {
            const onHome = homeCount > 0 && homeIds.includes(it.id);
            return (
            <div key={it.id} className={`flex items-center justify-between gap-4 p-4 border-b border-line last:border-0 ${onHome ? "bg-ink/[0.04]" : ""}`} data-testid={`entity-row-${it.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex flex-col shrink-0">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} data-testid={`entity-up-${it.id}`} className="p-0.5 border border-ink/20 hover:bg-ink hover:text-paper transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-ink"><ChevronUp size={13} /></button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1} data-testid={`entity-down-${it.id}`} className="p-0.5 border border-ink/20 border-t-0 hover:bg-ink hover:text-paper transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-ink"><ChevronDown size={13} /></button>
                </div>
                {it.cover_image && <img src={assetUrl(it.cover_image)} alt="" className="w-12 h-12 object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{it[`${nameKey}_ro`] || it[`${nameKey}_en`]}</div>
                  <div className="eyebrow mt-1 flex flex-wrap gap-3 text-ink/50 items-center">
                    <span className="tnum">#{it.order}</span>
                    {it.slug && <span>{it.slug}</span>}
                    {!it.published && <span className="text-red-700">draft</span>}
                    {onHome && <span className="bg-ink text-paper px-2 py-0.5" data-testid={`entity-onhome-${it.id}`}>PE HOME</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => edit(it)} data-testid={`entity-edit-${it.id}`} className="eyebrow border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors inline-flex items-center gap-1">
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => duplicate(it)} title="Duplică" data-testid={`entity-duplicate-${it.id}`} className="eyebrow border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors inline-flex items-center gap-1">
                  <Copy size={12} /> Duplică
                </button>
                <button onClick={() => del(it.id)} className="eyebrow border border-red-700 text-red-700 px-3 py-2 hover:bg-red-700 hover:text-paper transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            );
          })}
          {items.length === 0 && <div className="p-6 eyebrow">Niciun element.</div>}
        </div>
      </div>
    </div>
  );
}
